import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import { useSEO } from '@/hooks/useSEO';
import { usePageTracker } from '@/hooks/usePageTracker';
import {
  ArrowRight, Building2, FlaskConical, Search, Target, Globe, FileText,
  BarChart3, Zap, Users, Handshake, Microscope, Shield, Briefcase,
  CheckCircle2, TrendingUp, Layers, Brain, BookOpen, Stethoscope, Activity, Sparkles
} from 'lucide-react';

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const PHARMA_SERVICES = [
  {
    icon: <Search className="w-5 h-5" />,
    title: 'Asset Scouting & Global Sourcing',
    desc: 'We map the global biotech landscape against your BD thesis — by therapeutic area, clinical stage, modality, and geography. From China and India to APAC and emerging markets, we bring regional context, regulatory insight, and local networks that global BD teams typically lack.',
    deliverables: ['Curated asset shortlists with rationale', 'Cross-border pipeline scans & deal structure guidance', 'Deal-readiness assessment per asset'],
  },
  {
    icon: <BarChart3 className="w-5 h-5" />,
    title: 'Competitive Landscape & Deal Benchmarking',
    desc: 'Structured analysis of recent licensing deals, M&A transactions, and comparable partnerships in your therapeutic area. Know what the market is paying before you negotiate.',
    deliverables: ['Deal comp analysis', 'Competitive asset mapping', 'Term benchmarking reports'],
  },
  {
    icon: <Zap className="w-5 h-5" />,
    title: 'Conference BD Command',
    desc: 'Pre-conference intelligence packages: priority target lists, meeting scheduling, counterpart dossiers, and structured follow-up. For BIO International, BioChina, BIO-Europe, CPHI, and beyond. We are there — so you don\'t have to waste time on the wrong meetings.',
    deliverables: ['Pre-conference target lists', 'Meeting dossiers & prep notes', 'Structured post-conference follow-up'],
  },
  {
    icon: <Handshake className="w-5 h-5" />,
    title: 'Due Diligence & Deal Support',
    desc: 'As discussions progress, we support with evidence review, term benchmarking, and facilitation. Deal-ready packages shorten diligence cycles. We stay in the loop until close.',
    deliverables: ['Evidence gap analysis', 'Commercial scenario framing', 'Deal facilitation & coordination'],
  },
];

const BIOTECH_SERVICES = [
  {
    icon: <Target className="w-5 h-5" />,
    title: 'Asset Positioning & Value Story',
    desc: 'We sharpen your narrative into the Problem-Patient-Proof framework that pharma BD teams expect. Your science, reframed for the audience that matters.',
    deliverables: ['Positioning document', 'Plain-language value story', 'TPP framing & sanity check'],
  },
  {
    icon: <FileText className="w-5 h-5" />,
    title: 'Out-Licensing Readiness',
    desc: 'Non-confidential deck development, partner target lists, and positioning strategy. Everything you need to walk into BD conversations prepared.',
    deliverables: ['Non-confidential deck', 'Tiered partner target list', 'Readiness scorecard'],
  },
  {
    icon: <Users className="w-5 h-5" />,
    title: 'Pharma Partner Outreach',
    desc: 'Curated, evidence-backed outreach to the specific pharma companies most likely to care about your asset. Not volume spray — targeted engagement.',
    deliverables: ['Curated target list (20-40 names)', 'Personalised outreach sequences', 'Response tracking & follow-up'],
  },
  {
    icon: <Briefcase className="w-5 h-5" />,
    title: 'Conference Representation',
    desc: 'We represent your asset at major conferences. Pre-scheduled meetings with pharma BD teams, counterpart dossiers, and structured follow-up — so your science gets seen without burning $50K on a booth.',
    deliverables: ['Conference meeting scheduling', 'Counterpart dossiers', 'Post-conference pipeline tracking'],
  },
];

const SCIENTIFIC_SERVICES = [
  {
    icon: <BarChart3 className="w-5 h-5" />,
    title: 'Early Health Economic Modeling',
    desc: 'Pharma won\'t look at your asset if you can\'t show a cost-effectiveness story. We build early-stage models that demonstrate commercial viability — before you even start partnering conversations.',
    deliverables: ['Early cost-effectiveness models', 'Budget impact scenarios', 'Pricing & reimbursement signals'],
  },
  {
    icon: <Brain className="w-5 h-5" />,
    title: 'Competitive Landscape & Benchmarking',
    desc: 'Where does your asset sit vs. competitors? We map the landscape — pipeline threats, recent deals, comparable transactions — so you walk into every BD meeting knowing exactly where you stand.',
    deliverables: ['Competitive pipeline mapping', 'Deal benchmarking & comparables', 'Strategic positioning analysis'],
  },
  {
    icon: <Search className="w-5 h-5" />,
    title: 'Asset Positioning & BD Readiness',
    desc: 'Great science isn\'t enough — pharma needs to understand it in 5 minutes. We identify your evidence gaps, reframe your asset into the language BD teams respond to, and deliver a complete readiness package that turns scientific potential into deal-ready positioning.',
    deliverables: ['BD readiness scorecard & evidence gap assessment', 'BD-ready asset positioning deck', 'Data generation roadmap & differentiation thesis'],
  },
  {
    icon: <FileText className="w-5 h-5" />,
    title: 'Manuscripts & Publications',
    desc: 'Published evidence is the currency of credibility. We support your publication strategy end-to-end — from identifying high-impact journals to drafting manuscripts, conference abstracts, and white papers that build the scientific narrative pharma BD teams need to see.',
    deliverables: ['Peer-reviewed manuscript drafting & support', 'Conference posters & abstract preparation', 'Publication strategy & journal targeting'],
  },
];

function ServiceCard({ service, index, inView, color }) {
  return (
    <div
      className={`bg-gradient-to-br from-[#111A3A] to-[#0A1128] border border-white/5 rounded-2xl p-6 lg:p-8 hover:border-${color === 'teal' ? '[#00709B]' : '[#70C24C]'}/30 transition-all duration-500 ${inView ? `reveal-up stagger-${index + 1}` : 'opacity-0'}`}
      data-testid={`service-${color}-${index}`}
    >
      <div className={`w-10 h-10 rounded-xl ${color === 'teal' ? 'bg-[#00709B]/15' : 'bg-[#70C24C]/10'} flex items-center justify-center ${color === 'teal' ? 'text-[#00709B]' : 'text-[#70C24C]'} mb-5`}>
        {service.icon}
      </div>
      <h4 className="text-base lg:text-lg font-bold text-white mb-2">{service.title}</h4>
      <p className="text-sm text-[#A1A8C3] font-light leading-relaxed mb-5">{service.desc}</p>
      <div className="space-y-2">
        {service.deliverables.map((d, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <CheckCircle2 className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${color === 'teal' ? 'text-[#00709B]' : 'text-[#70C24C]'}`} />
            <span className="text-xs text-[#687399]">{d}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ServicesPage() {
  useSEO({
    title: 'Services — Pharma Partnering Intelligence & Biotech BD Services',
    description: 'Asset scouting, global sourcing, competitive landscape, conference BD command, due diligence, asset positioning, manuscripts, and HEOR — for pharma BD teams and biotech out-licensing.',
    canonical: 'https://foresightrx.com/services',
  });
  usePageTracker('Services');

  const [pharmaRef, pharmaInView] = useInView();
  const [biotechRef, biotechInView] = useInView();
  const [sciRef, sciInView] = useInView();
  const [modelRef, modelInView] = useInView();
  const [diffRef, diffInView] = useInView();

  return (
    <div className="min-h-screen bg-[#0A1128] copy-protect">
      <PublicNav />

      {/* Hero */}
      <section className="pt-32 lg:pt-40 pb-20 lg:pb-28 bg-[#0A1128] relative overflow-hidden" data-testid="services-header">
        <div className="absolute inset-0 grid-bg" />
        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] bg-[#00709B]/12 rounded-full blur-[120px] pulse-glow" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4 reveal-up">Services</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.05] reveal-up stagger-1" data-testid="services-heading">
              Living intelligence.
              <br />
              Hands-on execution.
              <br />
              <span className="text-[#70C24C]">Speed to market.</span>
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-[#A1A8C3] font-light leading-relaxed max-w-2xl reveal-up stagger-2">
              Partnering intelligence backed by deep scientific and healthcare expertise. From evidence
              synthesis and health economic modeling to deal sourcing and facilitation —
              we deliver the full stack of capabilities pharma and biotech need to close deals.
            </p>
            <p className="mt-4 text-sm text-[#687399] font-light reveal-up stagger-3">
              Powered by MarksMan Healthcare
            </p>
          </div>
        </div>
      </section>

      {/* Featured retainer banner — top of services */}
      <section className="bg-gradient-to-br from-[#0E1733] to-[#0A1128] border-y border-[#70C24C]/20 py-16" data-testid="featured-retainer-banner">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-[1fr_auto] items-center gap-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#70C24C]/10 border border-[#70C24C]/20 text-[#70C24C] text-[11px] font-bold uppercase tracking-wider mb-4">
                <Sparkles className="w-3.5 h-3.5" /> Featured · For Pharma In-Licensors
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight">
                Pharma BD Retainer — replaces five vendors with one team.
              </h2>
              <p className="mt-4 text-base text-[#A1A8C3] font-light max-w-3xl leading-relaxed">
                A 12-month productized engagement: <strong className="text-white">90+ structured deliverables/year</strong>,
                4 conferences covered live, asset evaluation across 8 dimensions, hands-on deal facilitation, and a dedicated platform copilot —
                all calibrated to one geography of your choice.
              </p>
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#687399]">
                <span>Greater China · Japan · Korea · India · SEA · ANZ · EU · LATAM · MENA · Global</span>
              </div>
            </div>
            <Link to="/services/pharma-bd-retainer"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#70C24C] text-[#0A1128] font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)] shrink-0"
                  data-testid="featured-retainer-cta">
              See the full retainer <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* For Pharma */}
      <section ref={pharmaRef} className="py-24 lg:py-32 bg-[#111A3A]" data-testid="pharma-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-6 ${pharmaInView ? 'reveal-up' : 'opacity-0'}`}>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00709B]/15 text-[#00709B] text-xs font-bold uppercase tracking-wider mb-4">
                <Building2 className="w-3.5 h-3.5" /> For Pharma & Large Biotech
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Find, evaluate, and access
                <br />
                the right biotech assets.
              </h2>
            </div>
            <Link to="/apply" className="inline-flex items-center gap-2 text-sm font-semibold text-[#00709B] hover:text-white transition-colors duration-300 shrink-0" data-testid="pharma-services-cta">
              Explore pharma solutions <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {PHARMA_SERVICES.map((s, i) => (
              <ServiceCard key={i} service={s} index={i} inView={pharmaInView} color="teal" />
            ))}
          </div>
        </div>
      </section>

      {/* For Biotech */}
      <section ref={biotechRef} className="py-24 lg:py-32 bg-[#0A1128]" data-testid="biotech-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-6 ${biotechInView ? 'reveal-up' : 'opacity-0'}`}>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#70C24C]/10 text-[#70C24C] text-xs font-bold uppercase tracking-wider mb-4">
                <FlaskConical className="w-3.5 h-3.5" /> For Emerging Biotech
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Position your asset. Build the evidence.
                <br />
                Reach the right partners.
              </h2>
            </div>
            <Link to="/apply" className="inline-flex items-center gap-2 text-sm font-semibold text-[#70C24C] hover:text-white transition-colors duration-300 shrink-0" data-testid="biotech-services-cta">
              Explore biotech solutions <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {BIOTECH_SERVICES.map((s, i) => (
              <ServiceCard key={i} service={s} index={i} inView={biotechInView} color="green" />
            ))}
          </div>
        </div>
      </section>

      {/* Scientific Services */}
      <section ref={sciRef} className="py-24 lg:py-32 bg-[#111A3A]" data-testid="scientific-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-6 ${sciInView ? 'reveal-up' : 'opacity-0'}`}>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 text-white text-xs font-bold uppercase tracking-wider mb-4">
                <Microscope className="w-3.5 h-3.5" /> Powered by MarksMan Healthcare
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Big pharma capabilities.
                <br />
                Built for small biotech.
              </h2>
              <p className="mt-4 text-base text-[#A1A8C3] font-light max-w-2xl">
                We bring the rigour of delivering HEOR, competitive intelligence, and evidence strategy
                to the world's largest pharma companies — now repackaged for small
                biotechs. The ones that need it most but can afford it least.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {SCIENTIFIC_SERVICES.map((s, i) => (
              <ServiceCard key={i} service={s} index={i} inView={sciInView} color="teal" />
            ))}
          </div>

          <div className={`mt-8 bg-gradient-to-r from-[#70C24C]/5 to-[#00709B]/5 border border-white/5 rounded-2xl p-5 text-center ${sciInView ? 'reveal-up stagger-5' : 'opacity-0'}`}>
            <p className="text-sm text-[#A1A8C3] font-light">
              These services are delivered by the same team that has served <span className="text-white font-medium">top-20 pharma companies</span> worldwide.
              <span className="text-[#70C24C] font-medium"> Now available to small biotechs at a fraction of the cost.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Engagement Model */}
      <section ref={modelRef} className="py-24 lg:py-32 bg-[#111A3A]" data-testid="engagement-model">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`text-center mb-16 ${modelInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Engagement Models</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-5">
              Flexible structures. Aligned incentives.
            </h2>
            <p className="text-lg text-[#A1A8C3] font-light max-w-2xl mx-auto">
              We do not believe in one-size-fits-all. Our engagement models are designed
              to match where you are and what you need.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <TrendingUp className="w-6 h-6" />,
                title: 'Retainer / Subscription',
                audience: 'Pharma',
                audienceColor: '#00709B',
                desc: 'Ongoing access to our intelligence layer and deal support. Ideal for pharma BD teams running continuous scouting programs across multiple therapeutic areas.',
                features: ['Continuous asset scouting', 'Priority access to new pipeline data', 'Dedicated account lead', 'Quarterly pipeline reviews'],
              },
              {
                icon: <Zap className="w-6 h-6" />,
                title: 'Project-Based Sprints',
                audience: 'Both',
                audienceColor: '#687399',
                desc: 'Focused engagements with clear deliverables and timelines. Evidence blueprints, positioning strategy, or targeted scouting — delivered in weeks, not quarters.',
                features: ['Defined scope & deliverables', 'Fast turnaround (1-4 weeks)', 'Fixed project fee', 'Senior team from day one'],
              },
              {
                icon: <Handshake className="w-6 h-6" />,
                title: 'Success-Based Partnering',
                audience: 'Biotech',
                audienceColor: '#70C24C',
                desc: 'We invest our time and network in your asset. Our fee is tied to outcomes — we earn when a deal or investment closes through our introductions.',
                features: ['No retainers or upfront fees', 'Aligned incentives', 'Full BD support through close', 'Fee on successful transaction'],
              },
            ].map((model, i) => (
              <div key={i} className={`bg-gradient-to-br from-[#0A1128] to-[#111A3A] border border-white/5 rounded-3xl p-8 hover:border-white/10 transition-all duration-500 ${modelInView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`} data-testid={`model-${i}`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-[#00709B]">
                    {model.icon}
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: `${model.audienceColor}15`, color: model.audienceColor }}>
                    {model.audience}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{model.title}</h3>
                <p className="text-sm text-[#A1A8C3] font-light leading-relaxed mb-6">{model.desc}</p>
                <div className="space-y-2.5">
                  {model.features.map((f, j) => (
                    <div key={j} className="flex items-start gap-2.5">
                      <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#687399]" />
                      <span className="text-xs text-[#687399]">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section ref={diffRef} className="py-24 lg:py-32 bg-[#0A1128]" data-testid="differentiators">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`text-center mb-16 ${diffInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Why ForesightRx</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              Not a database. Not a broker. Not generic consulting.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: <Layers className="w-5 h-5" />, title: 'Living Intelligence', desc: 'Not a static database. Our intelligence is continuously updated from 12+ global conferences annually. Every meeting feeds the engine.' },
              { icon: <Shield className="w-5 h-5" />, title: 'Evidence-First', desc: 'Every recommendation backed by clinical, competitive, and commercial evidence. We bring the rigour pharma expects.' },
              { icon: <Globe className="w-5 h-5" />, title: 'Cross-Border Reach', desc: '45+ markets tracked. Deep networks across China, India, APAC, and global pharma. We bridge ecosystems others miss.' },
              { icon: <Target className="w-5 h-5" />, title: 'Outcome-Oriented', desc: 'We don\'t stop at reports. We position, introduce, facilitate, and support through to deal close.' },
            ].map((item, i) => (
              <div key={i} className={`text-center p-6 rounded-2xl bg-[#111A3A] border border-white/5 hover:border-white/10 transition-all duration-300 ${diffInView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`} data-testid={`diff-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#00709B]/15 flex items-center justify-center text-[#00709B] mx-auto mb-4">{item.icon}</div>
                <h4 className="text-sm font-bold text-white mb-2">{item.title}</h4>
                <p className="text-xs text-[#A1A8C3] font-light leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#111A3A] relative overflow-hidden" data-testid="services-cta">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#00709B]/10 to-[#70C24C]/10 blur-[100px]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-6">
            Tell us what you need.
            <br />
            We will tell you how we can help.
          </h2>
          <p className="text-lg text-[#A1A8C3] font-light mb-10 max-w-xl mx-auto">
            Every engagement starts with a conversation. No commitment, no sales pitch —
            just an honest assessment of whether there is a fit.
          </p>
          <Link
            to="/apply"
            className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-[#70C24C] text-[#0A1128] text-base font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)]"
            data-testid="services-apply-btn"
          >
            Start a Conversation <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
